import React, { useState } from 'react';
import {
  AlertBannerError,
  Badge,
  ButtonSecondary,
  Typography,
  SpacerVertical,
  Tooltip,
} from '@klarna/bubble-ui';
import PropTypes from 'prop-types';
import _flatten from 'lodash/flatten';
import _merge from 'lodash/merge';
import { getMerchantWithSteps } from 'moet-test-utils/fixtures/widget-merchants';
import { type } from 'ramda';

import { createWidgetMerchant } from '@/moet-gui/merchant';
import { evaluateStep, getCurrentStep } from 'moet-core/src/flow/utils';
import { CONDITION_OPERATOR } from 'moet-core/src/flow';

const { AND, OR, NOT } = CONDITION_OPERATOR;

const { type: env } = window.config?.common?.environment || {};

const SELECTOR_CONDITION_FIXTURE_MAP = {
  canSubmitApplication: ['stakeholderApplicant', 'bankAccounts'],
  hasBankAccounts: ['bankAccounts'],
  hasCompanyDetails: ['companyDetails'],
  hasControlOverManagementStakeholders: ['stakeholderCOM'],
  hasEntityTypeInfo: ['publicListedCompanyEntityType'],
  hasPartnerRole: ['stakeholderPartner'],
  hasPartnershipDetails: ['partnershipDetails'],
  hasRegistrationInformation: ['registrationInformation'],
  hasSelectedProduct: ['products'],
  hasStoreDetails: ['storeDetails'],
  hasSubmittedStakeholderBlockFca: ['stakeholderUBO', 'stakeholderApplicant'],
  hasSubmittedStakeholderBlockSfsa: ['stakeholderUBO', 'stakeholderApplicant'],
  hasUltimateBeneficialOwner: ['stakeholderUBO'],
  hasVatInformation: ['vatId'],
  isEntityTypePartnership: ['companyNotRegistered', 'partnershipEntityType'],
  isEntityTypePrivateCompany: ['privateCompanyEntityType'],
  isEntityTypePublicListedCompany: ['publicListedCompanyEntityType'],
  NOT_isMerchantRegistered: ['companyNotRegistered'],
  isMerchantRegistered: ['registrationInformation'],
  isSoleTraderOrSoleProprietorship: ['companyNotRegistered', 'soleTraderEntityType'],
};

const CORRELATING_CONDITIONS = {
  hasPartnerRole: { isEntityTypePartnership: true },
};

const getStateToFulfillObjectCondition = {
  [AND]: (conditions = []) => _merge(...conditions.map(getStateToFulfillCondition)),
  [OR]: (conditions = []) => (conditions.length ? getStateToFulfillCondition(conditions[0]) : {}),
  [NOT]: (conditions = []) =>
    _merge(
      ...conditions.map(c => {
        const state = getStateToFulfillCondition(c);
        return Object.keys(state).reduce((acc, key) => ({ ...acc, [key]: !state[key] }), {});
      }),
    ),
};

const getStateToFulfillCondition = (condition = []) => {
  if (Array.isArray(condition)) {
    return condition.reduce((acc, c) => ({ ...acc, ...getStateToFulfillCondition(c) }), {});
  }

  if (type(condition) === 'Object') {
    const { operator, conditions = [] } = condition;
    const mapperFn = getStateToFulfillObjectCondition[operator];
    return mapperFn ? mapperFn(conditions) : {};
  }

  if (type(condition) === 'String') {
    return { [condition]: true, ...(CORRELATING_CONDITIONS[condition] || {}) };
  }

  return {};
};

const getPrerequisitesForStep = (stepPath, flowConfig, prerequisites = {}) => {
  const step = getCurrentStep(flowConfig, {}, { pathOverride: stepPath });

  const stepPrerequisites = getStateToFulfillCondition(step.prerequisite);

  const newPrerequisites = { ...prerequisites, ...stepPrerequisites };

  const back = evaluateStep('back', step, newPrerequisites);

  if (!back) {
    return newPrerequisites;
  }

  return getPrerequisitesForStep(back, flowConfig, newPrerequisites);
};

const tooltipStyle = (showTooltip = false) => ({
  display: showTooltip ? 'block' : 'none',
  justifyContent: 'center',
  position: 'absolute',
  top: 'calc(50% - 15px)',
  left: 'calc(100% + 5px)',
  minWidth: '400px',
  zIndex: 106,
});

export const ObjectPrerequisiteBadge = ({ operator = AND, conditions = [] }) => {
  if (operator === AND) {
    return <PrerequisiteBadge prerequisite={conditions} />;
  }

  if (operator === OR) {
    return conditions.map((p, i) => (
      <div key={i} style={{ marginBottom: '2px' }}>
        {i > 0 && operator}
        <PrerequisiteBadge prerequisite={p} />
      </div>
    ));
  }

  if (operator === NOT && conditions.length) {
    const condition = conditions[0];
    if (type(condition) === 'String') {
      return <PrerequisiteBadge prerequisite={`¬ ${condition}`} />;
    }
  }
};

ObjectPrerequisiteBadge.propTypes = {
  conditions: PropTypes.array.isRequired,
  operator: PropTypes.string.isRequired,
};

export const PrerequisiteBadge = ({ prerequisite }) => {
  if (Array.isArray(prerequisite)) {
    return prerequisite.length
      ? prerequisite.map((p, i) => (
          <div key={i} style={{ marginBottom: '2px' }}>
            <PrerequisiteBadge prerequisite={p} />
          </div>
        ))
      : null;
  }

  if (type(prerequisite) === 'Object') {
    return (
      <ObjectPrerequisiteBadge
        operator={prerequisite.operator}
        conditions={prerequisite.conditions}
      />
    );
  }

  return <Badge>{prerequisite}</Badge>;
};

PrerequisiteBadge.propTypes = {
  prerequisite: PropTypes.string.isRequired,
};

export const FlowStep = ({
  country,
  flowConfig,
  id,
  path,
  prerequisite,
  region,
  widgetType,
  showDetails = false,
  showOpenWidgetButton = true,
  getBackendUrlByRegion = () => {},
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const openWidget = () => {
    setIsLoading(true);
    setError(null);

    const prerequisites = getPrerequisitesForStep(path, flowConfig);

    const stepFixtures = _flatten(
      Object.keys(prerequisites).map(key => {
        const value = prerequisites[key];
        return SELECTOR_CONDITION_FIXTURE_MAP[value === false ? `NOT_${key}` : key];
      }),
    );

    const merchantData = stepFixtures.length
      ? getMerchantWithSteps(country, stepFixtures)
      : undefined;

    createWidgetMerchant({
      baseUrl: getBackendUrlByRegion(region),
      country,
      environment: window.location.host.startsWith('localhost') ? 'local' : env,
      region,
      widgetType,
      merchantData,
    })
      .then(link => {
        setIsLoading(false);
        setError(null);
        return window.open(link);
      })
      .catch(error => {
        setError(error);
        setIsLoading(false);
      });
  };

  return (
    <>
      <Typography textToken="text-style/text/paragraphs/body/bold" align="center">
        {id.toUpperCase()}
      </Typography>
      <div id="tooltip" style={tooltipStyle(showDetails)}>
        <Tooltip inverse arrow="left-top" isNotText>
          {error && <AlertBannerError content="Failed to open the widget" />}
          {showOpenWidgetButton && (
            <>
              <Typography textToken="text-style/headings/titles/bold/grande">{id}</Typography>
              {prerequisite && (
                <>
                  <Typography textToken="text-style/text/paragraphs/default/regular">
                    Prerequisites
                  </Typography>
                  <Typography textToken="text-style/text/super-paragraphs/body/regular">
                    <PrerequisiteBadge prerequisite={prerequisite} />
                  </Typography>
                </>
              )}
              <SpacerVertical small />
              <ButtonSecondary size="small" onClick={openWidget} loading={isLoading}>
                Open widget
              </ButtonSecondary>
            </>
          )}
        </Tooltip>
      </div>
    </>
  );
};

FlowStep.propTypes = {
  country: PropTypes.string.isRequired,
  flowConfig: PropTypes.object.isRequired,
  getBackendUrlByRegion: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  prerequisite: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  widgetType: PropTypes.string.isRequired,
  showDetails: PropTypes.bool,
  showOpenWidgetButton: PropTypes.bool,
};
